import React from 'react';
import './index.css';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';

import SuperTokens, {useTranslation} from 'supertokens-auth-react';
import {EmailVerificationComponentsOverrideProvider} from 'supertokens-auth-react/recipe/emailverification';
import {EmailPasswordComponentsOverrideProvider} from 'supertokens-auth-react/recipe/emailpassword';

interface ILoginBottomProps {
  children?: React.ReactNode;
}

function LoginBottom(props: ILoginBottomProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mt: 2,
      }}>
      {props.children}
    </Box>
  );
}

interface IOverrideSuperTokensFormProps {
  children?: React.ReactNode;
}

const BlueAccent = () => <Box sx={{width: 48, height: 4, bgcolor: '#1976d2', borderRadius: 2}} />;

export const OverrideSuperTokensForm = ({children}: IOverrideSuperTokensFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <EmailVerificationComponentsOverrideProvider
      components={{
        EmailVerificationSendVerifyEmail_Override: ({DefaultComponent, ...props}) => {
          const trans = useTranslation();
          return (
            <Box
              sx={{
                display: 'flex',
                height: isMobile ? '100%' : '100vh',
                flexDirection: isMobile ? 'column' : 'row',
                overflow: 'hidden',
                position: 'relative',
              }}>
              <Avatar
                alt='Logo'
                src='/logo.svg'
                variant='square'
                sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  height: '80px',
                  width: '80px',
                  zIndex: 3,
                }}
              />
              {/* Form Section */}
              <Box
                sx={{
                  flex: isMobile ? '1' : '0 0 50%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: isMobile ? 'space-between' : 'center',
                  alignItems: 'center',
                  padding: isMobile ? 0 : '3rem',
                  backgroundColor: '#fff',
                  position: 'relative',
                  overflow: isMobile ? 'auto' : 'hidden',
                  borderRadius: isMobile ? 0 : '0 24px 24px 0',
                  boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                  zIndex: 2,
                  marginRight: isMobile ? 0 : '-50px',
                  height: isMobile ? '100%' : '100%',
                  minHeight: isMobile ? '100vh' : 'auto',
                }}>
                <Box sx={{width: '100%'}}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: isMobile ? 2 : 4,
                      width: '100%',
                      maxWidth: 420,
                      mt: isMobile ? '140px' : 0,
                    }}>
                    {/* You can add a title or leave empty for spacing */}
                  </Box>
                  {isMobile ? (
                    <Box
                      sx={{
                        width: '100%',
                        px: 2,
                        pb: 2,
                        '& > div': {
                          maxWidth: '100%',
                          width: '100%',
                        },
                        '& form': {
                          width: '100%',
                        },
                        '& input': {
                          width: '100%',
                        },
                        '& button': {
                          width: '100%',
                          mt: 2,
                        },
                      }}>
                      <DefaultComponent {...props} />
                    </Box>
                  ) : (
                    <Card>
                      <CardContent sx={{p: 3}}>
                        <DefaultComponent {...props} />
                      </CardContent>
                    </Card>
                  )}
                </Box>
                {/* Footer */}
                <Box
                  sx={{
                    position: isMobile ? 'relative' : 'absolute',
                    bottom: 0,
                    width: '100%',
                    padding: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderTop: '1px solid #e0e0e0',
                    left: 0,
                  }}>
                  <Box sx={{maxWidth: 420, mx: 'auto'}}>
                    <Typography variant='body2' color='text.secondary' align='center'>
                      <Button
                        sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                        variant='contained'
                        color={trans('language') === 'de' ? 'primary' : 'secondary'}
                        onClick={() => {
                          SuperTokens.changeLanguage('de');
                        }}>
                        {'🇩🇪'}
                      </Button>
                      <Button
                        sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                        variant='contained'
                        color={trans('language') === 'en' ? 'primary' : 'secondary'}
                        onClick={() => {
                          SuperTokens.changeLanguage('en');
                        }}>
                        {'🇬🇧'}
                      </Button>
                      {'Copyright © '}
                      <Link color='inherit' href='https://boehringer.net/'>
                        Böhringer Creativbau
                      </Link>{' '}
                      {new Date().getFullYear()}
                      {'.'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Image Section - Only visible on desktop */}
              {!isMobile && (
                <Box
                  sx={{
                    flex: '0 0 55%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      zIndex: 1,
                    }}
                  />
                  <Box
                    component='img'
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                    alt='Böhringer Creativbau Office'
                    src='../boe_startseite_buero_1.rgb.jpg'
                  />
                </Box>
              )}
            </Box>
          );
        },
        EmailVerificationVerifyEmailLinkClicked_Override: ({DefaultComponent, ...props}) => {
          const trans = useTranslation();
          return (
            <Box
              sx={{
                display: 'flex',
                height: isMobile ? '100%' : '100vh',
                flexDirection: isMobile ? 'column' : 'row',
                overflow: 'hidden',
                position: 'relative',
              }}>
              <Avatar
                alt='Logo'
                src='/logo.svg'
                variant='square'
                sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  height: '80px',
                  width: '80px',
                  zIndex: 3,
                }}
              />
              {/* Form Section */}
              <Box
                sx={{
                  flex: isMobile ? '1' : '0 0 50%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: isMobile ? 'space-between' : 'center',
                  alignItems: 'center',
                  padding: isMobile ? 0 : '3rem',
                  backgroundColor: '#fff',
                  position: 'relative',
                  overflow: isMobile ? 'auto' : 'hidden',
                  borderRadius: isMobile ? 0 : '0 24px 24px 0',
                  boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                  zIndex: 2,
                  marginRight: isMobile ? 0 : '-50px',
                  height: isMobile ? '100%' : '100%',
                  minHeight: isMobile ? '100vh' : 'auto',
                }}>
                <Box sx={{width: '100%'}}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: isMobile ? 2 : 4,
                      width: '100%',
                      maxWidth: 420,
                      mt: isMobile ? '140px' : 0,
                    }}>
                    {/* You can add a title or leave empty for spacing */}
                  </Box>
                  {isMobile ? (
                    <Box
                      sx={{
                        width: '100%',
                        px: 2,
                        pb: 2,
                        '& > div': {
                          maxWidth: '100%',
                          width: '100%',
                        },
                        '& form': {
                          width: '100%',
                        },
                        '& input': {
                          width: '100%',
                        },
                        '& button': {
                          width: '100%',
                          mt: 2,
                        },
                      }}>
                      <DefaultComponent {...props} />
                    </Box>
                  ) : (
                    <Card>
                      <CardContent sx={{p: 3}}>
                        <DefaultComponent {...props} />
                      </CardContent>
                    </Card>
                  )}
                </Box>
                {/* Footer */}
                <Box
                  sx={{
                    position: isMobile ? 'relative' : 'absolute',
                    bottom: 0,
                    width: '100%',
                    padding: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderTop: '1px solid #e0e0e0',
                    left: 0,
                  }}>
                  <Box sx={{maxWidth: 420, mx: 'auto'}}>
                    <Typography variant='body2' color='text.secondary' align='center'>
                      <Button
                        sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                        variant='contained'
                        color={trans('language') === 'de' ? 'primary' : 'secondary'}
                        onClick={() => {
                          SuperTokens.changeLanguage('de');
                        }}>
                        {'🇩🇪'}
                      </Button>
                      <Button
                        sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                        variant='contained'
                        color={trans('language') === 'en' ? 'primary' : 'secondary'}
                        onClick={() => {
                          SuperTokens.changeLanguage('en');
                        }}>
                        {'🇬🇧'}
                      </Button>
                      {'Copyright © '}
                      <Link color='inherit' href='https://boehringer.net/'>
                        Böhringer Creativbau
                      </Link>{' '}
                      {new Date().getFullYear()}
                      {'.'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Image Section - Only visible on desktop */}
              {!isMobile && (
                <Box
                  sx={{
                    flex: '0 0 55%',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      zIndex: 1,
                    }}
                  />
                  <Box
                    component='img'
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                    alt='Böhringer Creativbau Office'
                    src='../boe_startseite_buero_1.rgb.jpg'
                  />
                </Box>
              )}
            </Box>
          );
        },
      }}>
      <EmailPasswordComponentsOverrideProvider
        components={{
          EmailPasswordSignIn_Override: ({DefaultComponent, ...props}) => {
            const trans = useTranslation();
            return (
              <Box
                sx={{
                  display: 'flex',
                  height: isMobile ? '100%' : '100vh',
                  flexDirection: isMobile ? 'column' : 'row',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Avatar
                  alt='Logo'
                  src='/logo.svg'
                  variant='square'
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    height: '80px',
                    width: '80px',
                    zIndex: 3,
                  }}
                />
                {/* Form Section */}
                <Box
                  sx={{
                    flex: isMobile ? '1' : '0 0 50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'space-between' : 'center',
                    alignItems: 'center',
                    padding: isMobile ? 0 : '3rem',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: isMobile ? 'auto' : 'hidden',
                    borderRadius: isMobile ? 0 : '0 24px 24px 0',
                    boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                    zIndex: 2,
                    marginRight: isMobile ? 0 : '-50px',
                    height: isMobile ? '100%' : '100%',
                    minHeight: isMobile ? '100vh' : 'auto',
                  }}>
                  <Box sx={{width: '100%'}}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: isMobile ? 2 : 4,
                        width: '100%',
                        maxWidth: 420,
                        mt: isMobile ? '140px' : 0,
                      }}></Box>

                    {isMobile ? (
                      <Box
                        sx={{
                          width: '100%',
                          px: 2,
                          pb: 2,
                          '& > div': {
                            maxWidth: '100%',
                            width: '100%',
                          },
                          '& form': {
                            width: '100%',
                          },
                          '& input': {
                            width: '100%',
                          },
                          '& button': {
                            width: '100%',
                            mt: 2,
                          },
                        }}>
                        <DefaultComponent {...props} />
                      </Box>
                    ) : (
                      <Card>
                        <CardContent sx={{p: 3}}>
                          <DefaultComponent {...props} />
                        </CardContent>
                      </Card>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      position: isMobile ? 'relative' : 'absolute',
                      bottom: 0,
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderTop: '1px solid #e0e0e0',
                      left: 0,
                    }}>
                    <Box sx={{maxWidth: 420, mx: 'auto'}}>
                      <Typography variant='body2' color='text.secondary' align='center'>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                          variant='contained'
                          color={trans('language') === 'de' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('de');
                          }}>
                          {'🇩🇪'}
                        </Button>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                          variant='contained'
                          color={trans('language') === 'en' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('en');
                            
                          }}>
                          {'🇬🇧'}
                        </Button>
                        <Link color='inherit' href=''>
                          Böhringer Creativbau
                        </Link>{' '}
                        {new Date().getFullYear()}
                        {'.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Image Section - Only visible on desktop */}
                {!isMobile && (
                  <Box
                    sx={{
                      flex: '0 0 55%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      component='img'
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                      }}
                      alt='Böhringer Creativbau Office'
                      src='../boe_startseite_buero_1.rgb.jpg'
                    />
                  </Box>
                )}
              </Box>
            );
          },
          EmailPasswordSignUp_Override: ({DefaultComponent, ...props}) => {
            const trans = useTranslation();
            return (
              <Box
                sx={{
                  display: 'flex',
                  height: isMobile ? '100%' : '100vh',
                  flexDirection: isMobile ? 'column' : 'row',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Avatar
                  alt='Logo'
                  src='/logo.svg'
                  variant='square'
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    height: '80px',
                    width: '80px',
                    zIndex: 3,
                  }}
                />
                {/* Form Section */}
                <Box
                  sx={{
                    flex: isMobile ? '1' : '0 0 50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'space-between' : 'center',
                    alignItems: 'center',
                    padding: isMobile ? 0 : '3rem',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: isMobile ? 'auto' : 'hidden',
                    borderRadius: isMobile ? 0 : '0 24px 24px 0',
                    boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                    zIndex: 2,
                    marginRight: isMobile ? 0 : '-50px',
                    height: isMobile ? '100%' : '100%',
                    minHeight: isMobile ? '100vh' : 'auto',
                  }}>
                  <Box sx={{width: '100%'}}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: isMobile ? 2 : 4,
                        width: '100%',
                        maxWidth: 420,
                        mt: isMobile ? '140px' : 0,
                      }}></Box>

                    {isMobile ? (
                      <Box
                        sx={{
                          width: '100%',
                          px: 2,
                          pb: 2,
                          '& > div': {
                            maxWidth: '100%',
                            width: '100%',
                          },
                          '& form': {
                            width: '100%',
                          },
                          '& input': {
                            width: '100%',
                          },
                          '& button': {
                            width: '100%',
                            mt: 2,
                          },
                        }}>
                        <DefaultComponent {...props} />
                      </Box>
                    ) : (
                      <Card>
                        <CardContent sx={{p: 3}}>
                          <DefaultComponent {...props} />
                        </CardContent>
                      </Card>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      position: isMobile ? 'relative' : 'absolute',
                      bottom: 0,
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderTop: '1px solid #e0e0e0',
                      left: 0,
                    }}>
                    <Box sx={{maxWidth: 420, mx: 'auto'}}>
                      <Typography variant='body2' color='text.secondary' align='center'>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                          variant='contained'
                          color={trans('language') === 'de' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('de');
                          }}>
                          {'🇩🇪'}
                        </Button>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                          variant='contained'
                          color={trans('language') === 'en' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('en');
                          }}>
                          {'🇬🇧'}
                        </Button>
                        {'Copyright © '}
                        <Link color='inherit' href='https://boehringer.net/'>
                          Böhringer Creativbau
                        </Link>{' '}
                        {new Date().getFullYear()}
                        {'.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Image Section - Only visible on desktop */}
                {!isMobile && (
                  <Box
                    sx={{
                      flex: '0 0 55%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      component='img'
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                      }}
                      alt='Böhringer Creativbau Office'
                      src='../boe_startseite_buero_1.rgb.jpg'
                    />
                  </Box>
                )}
              </Box>
            );
          },
          EmailPasswordResetPasswordEmail_Override: ({DefaultComponent, ...props}) => {
            const trans = useTranslation();
            return (
              <Box
                sx={{
                  display: 'flex',
                  height: isMobile ? '100%' : '100vh',
                  flexDirection: isMobile ? 'column' : 'row',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Avatar
                  alt='Logo'
                  src='/logo.svg'
                  variant='square'
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    height: '80px',
                    width: '80px',
                    zIndex: 3,
                  }}
                />
                {/* Form Section */}
                <Box
                  sx={{
                    flex: isMobile ? '1' : '0 0 50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'space-between' : 'center',
                    alignItems: 'center',
                    padding: isMobile ? 0 : '3rem',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: isMobile ? 'auto' : 'hidden',
                    borderRadius: isMobile ? 0 : '0 24px 24px 0',
                    boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                    zIndex: 2,
                    marginRight: isMobile ? 0 : '-50px',
                    height: isMobile ? '100%' : '100%',
                    minHeight: isMobile ? '100vh' : 'auto',
                  }}>
                  <Box sx={{width: '100%'}}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: isMobile ? 2 : 4,
                        width: '100%',
                        maxWidth: 420,
                        mt: isMobile ? '140px' : 0,
                      }}></Box>

                    {isMobile ? (
                      <Box
                        sx={{
                          width: '100%',
                          px: 2,
                          pb: 2,
                          '& > div': {
                            maxWidth: '100%',
                            width: '100%',
                          },
                          '& form': {
                            width: '100%',
                          },
                          '& input': {
                            width: '100%',
                          },
                          '& button': {
                            width: '100%',
                            mt: 2,
                          },
                        }}>
                        <DefaultComponent {...props} />
                      </Box>
                    ) : (
                      <Card>
                        <CardContent sx={{p: 3}}>
                          <DefaultComponent {...props} />
                        </CardContent>
                      </Card>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      position: isMobile ? 'relative' : 'absolute',
                      bottom: 0,
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderTop: '1px solid #e0e0e0',
                      left: 0,
                    }}>
                    <Box sx={{maxWidth: 420, mx: 'auto'}}>
                      <Typography variant='body2' color='text.secondary' align='center'>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                          variant='contained'
                          color={trans('language') === 'de' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('de');
                          }}>
                          {'🇩🇪'}
                        </Button>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                          variant='contained'
                          color={trans('language') === 'en' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('en');
                          }}>
                          {'🇬🇧'}
                        </Button>
                        {'Copyright © '}
                        <Link color='inherit' href='https://boehringer.net/'>
                          Böhringer Creativbau
                        </Link>{' '}
                        {new Date().getFullYear()}
                        {'.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Image Section - Only visible on desktop */}
                {!isMobile && (
                  <Box
                    sx={{
                      flex: '0 0 55%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      component='img'
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                      }}
                      alt='Böhringer Creativbau Office'
                      src='../boe_startseite_buero_1.rgb.jpg'
                    />
                  </Box>
                )}
              </Box>
            );
          },
          EmailPasswordSubmitNewPassword_Override: ({DefaultComponent, ...props}) => {
            const trans = useTranslation();
            return (
              <Box
                sx={{
                  display: 'flex',
                  height: isMobile ? '100%' : '100vh',
                  flexDirection: isMobile ? 'column' : 'row',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <Avatar
                  alt='Logo'
                  src='/logo.svg'
                  variant='square'
                  sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    height: '80px',
                    width: '80px',
                    zIndex: 3,
                  }}
                />
                {/* Form Section */}
                <Box
                  sx={{
                    flex: isMobile ? '1' : '0 0 50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'space-between' : 'center',
                    alignItems: 'center',
                    padding: isMobile ? 0 : '3rem',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: isMobile ? 'auto' : 'hidden',
                    borderRadius: isMobile ? 0 : '0 24px 24px 0',
                    boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
                    zIndex: 2,
                    marginRight: isMobile ? 0 : '-50px',
                    height: isMobile ? '100%' : '100%',
                    minHeight: isMobile ? '100vh' : 'auto',
                  }}>
                  <Box sx={{width: '100%'}}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: isMobile ? 2 : 4,
                        width: '100%',
                        maxWidth: 420,
                        mt: isMobile ? '140px' : 0,
                      }}></Box>

                    {isMobile ? (
                      <Box
                        sx={{
                          width: '100%',
                          px: 2,
                          pb: 2,
                          '& > div': {
                            maxWidth: '100%',
                            width: '100%',
                          },
                          '& form': {
                            width: '100%',
                          },
                          '& input': {
                            width: '100%',
                          },
                          '& button': {
                            width: '100%',
                            mt: 2,
                          },
                        }}>
                        <DefaultComponent {...props} />
                      </Box>
                    ) : (
                      <Card>
                        <CardContent sx={{p: 3}}>
                          <DefaultComponent {...props} />
                        </CardContent>
                      </Card>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      position: isMobile ? 'relative' : 'absolute',
                      bottom: 0,
                      width: '100%',
                      padding: '1rem',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderTop: '1px solid #e0e0e0',
                      left: 0,
                    }}>
                    <Box sx={{maxWidth: 420, mx: 'auto'}}>
                      <Typography variant='body2' color='text.secondary' align='center'>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 1}}
                          variant='contained'
                          color={trans('language') === 'de' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('de');
                          }}>
                          {'🇩🇪'}
                        </Button>
                        <Button
                          sx={{p: 0, minWidth: 30, fontSize: 18, mr: 2}}
                          variant='contained'
                          color={trans('language') === 'en' ? 'primary' : 'secondary'}
                          onClick={() => {
                            SuperTokens.changeLanguage('en');
                          }}>
                          {'🇬🇧'}
                        </Button>
                        {'Copyright © '}
                        <Link color='inherit' href='https://boehringer.net/'>
                          Böhringer Creativbau
                        </Link>{' '}
                        {new Date().getFullYear()}
                        {'.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Image Section - Only visible on desktop */}
                {!isMobile && (
                  <Box
                    sx={{
                      flex: '0 0 55%',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      component='img'
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                      }}
                      alt='Böhringer Creativbau Office'
                      src='../boe_startseite_buero_1.rgb.jpg'
                    />
                  </Box>
                )}
              </Box>
            );
          },
        }}>
        {children}
      </EmailPasswordComponentsOverrideProvider>
    </EmailVerificationComponentsOverrideProvider>
  );
};
